import { Component, inject, Input } from '@angular/core';
import { LearningPlanBuilderDataService } from '../../services/learning-plan-builder-data.service';
import { ReactiveFormsModule, FormArray } from '@angular/forms';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { ArrowWrapperComponent } from '../../../../../shared/components/arrow-wrapper/arrow-wrapper.component';
import { CourseSelectComponent } from '../../../../../shared/components/course-select/course-select.component';
import { LearningPlanCourseSelectionDialogComponent } from '../learning-plan-course-selection-dialog/learning-plan-course-selection-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { CourseGalleryItemDTO } from '../../models/LearningPlanDTO';
import { DIALOG_SIZE_LARGE } from '../../../../../shared/components/dialog/dialog-sizes';
import {
  CdkDragDrop,
  CdkDropList,
  CdkDrag,
  CdkDragHandle,
  CdkDragPlaceholder,
} from '@angular/cdk/drag-drop';
import { MatIcon } from '@angular/material/icon';
import { MatTooltip } from '@angular/material/tooltip';
import { SlideToggleComponent } from '../../../../../shared/components/slide-toggle/slide-toggle.component';
import { Observable, startWith, map } from 'rxjs';

@Component({
  selector: 'app-learning-plan-builder-course-selection',
  imports: [
    ReactiveFormsModule,
    TranslocoDirective,
    ArrowWrapperComponent,
    CourseSelectComponent,
    CdkDropList,
    CdkDrag,
    CdkDragHandle,
    CdkDragPlaceholder,
    MatIcon,
    MatTooltip,
    SlideToggleComponent,
  ],
  templateUrl: './learning-plan-builder-course-selection.component.html',
})
export class LearningPlanBuilderCourseSelectionComponent {
  @Input() isEditable = false;
  @Input() isPrecuratedTraining = false;

  protected readonly dataService = inject(LearningPlanBuilderDataService);
  private readonly transloco = inject(TranslocoService);
  dialog = inject(MatDialog);

  protected readonly arrowRedrawTrigger = this.dataService.arrowRedrawTrigger;

  constructor() {
    this.dataService.getPhasesArray().valueChanges.subscribe(() => {
      this.dataService.triggerArrowRedraw();
    });
  }

  getObservablePhases(): Observable<void> {
    return this.dataService.getPhasesArray().valueChanges.pipe(
      startWith(this.dataService.getPhasesArray().value),
      map(() => void 0),
    );
  }

  onDrop(event: CdkDragDrop<FormArray>) {
    if (event.previousIndex !== event.currentIndex) {
      const phasesArray = this.dataService.getPhasesArray();

      // Get the control that was moved
      const movedControl = phasesArray.at(event.previousIndex);

      // Remove it from the old position
      phasesArray.removeAt(event.previousIndex);

      // Insert it at the new position
      phasesArray.insert(event.currentIndex, movedControl);

      this.dataService.triggerArrowRedraw();
    }
  }

  onCourseSelectorButtonClickedEvent(index: number) {
    const dialogRef = this.dialog.open(
      LearningPlanCourseSelectionDialogComponent,
      {
        autoFocus: true,
        ...DIALOG_SIZE_LARGE,
        data: {
          topicAreaId: this.getSelectedTopicAreaId(),
        },
      },
    );
    dialogRef.afterClosed().subscribe((course: CourseGalleryItemDTO | null) => {
      if (course) {
        this.dataService.setCourseId(index, course);
        this.dataService.triggerArrowRedraw();
      }
    });
  }

  onRemovePhaseButtonClickedEvent(index: number) {
    this.dataService.removePhase(index);
    this.dataService.triggerArrowRedraw();
  }

  onRemoveCourseButtonClickedEvent(index: number) {
    this.dataService.setCourseId(index, null);
    this.dataService.triggerArrowRedraw();
  }

  getPhaseKey() {
    return this.dataService.formGroupSettings.controls.duration.value;
  }

  getSelectedTopicAreaId(): string | null {
    return this.dataService.formGroupSettings.controls.topicAreaId.value;
  }

  getPhaseName(index: number) {
    const phaseKey = this.getPhaseKey();
    const phase = this.transloco.translate(
      `portal.trainings.builder.stepper.settings.form.phaseFrequency.options.${phaseKey}`,
    );
    return `${phase} ${index + 1}`;
  }

  onSelectCourseClickedEvent() {
    const index = this.dataService.getPhasesArray().controls.length;
    const dialogRef = this.dialog.open(
      LearningPlanCourseSelectionDialogComponent,
      {
        autoFocus: true,
        ...DIALOG_SIZE_LARGE,
        data: {
          topicAreaId: this.getSelectedTopicAreaId(),
        },
      },
    );
    dialogRef.afterClosed().subscribe((course: CourseGalleryItemDTO | null) => {
      if (course) {
        this.dataService.addPhase();
        this.dataService.setCourseId(index, course);
        this.dataService.triggerArrowRedraw();
      }
    });
  }
}
